import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation
} from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Branches from './pages/Branches';
import BranchUsersEdit from './pages/BranchUsersEdit';
import Data from './pages/Data';
import Companies from './pages/Companies';
import Requests from './pages/Requests';
import Users from './pages/Users';
import Reports from './pages/Reports';
import LogPage from './pages/Log';
import HotspotUsers from './pages/HotspotUsers';
import HotspotProfiles from './pages/HotspotProfiles';
import PublicConsumo from './pages/PublicConsumo';
import BackupRestore from './pages/BackupRestore';
import Audit from './pages/Audit';
import Flcam from './pages/Flcam';
import './App.css';

const LoadingScreen: React.FC = () => (
  <div className="loading-screen" role="status" aria-live="polite">
    <span className="loading-screen__mark" aria-hidden="true" />
    <span>Preparando o FLConnect…</span>
  </div>
);

const ProtectedLayout: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

const LoginRoute: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Login />;
};

const HomeRedirect: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return <Navigate to={user ? '/dashboard' : '/login'} replace />;
};

const App: React.FC = () => {
  return (
    <Router>
      <Toaster position="top-right" />
      <div className="App">
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/consumo" element={<PublicConsumo />} />
          <Route path="/mockup" element={<Layout preview><Dashboard demo /></Layout>} />

          <Route element={<ProtectedLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/companies" element={<Companies />} />
            <Route path="/branches" element={<Branches />} />
            <Route path="/branches/:branchId/users" element={<BranchUsersEdit />} />
            <Route path="/requests" element={<Requests />} />
            <Route path="/data" element={<Data />} />
            <Route path="/users" element={<Users />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/log" element={<LogPage />} />
            <Route path="/hotspot-users" element={<HotspotUsers />} />
            <Route path="/hotspot-profiles" element={<HotspotProfiles />} />
            <Route path="/flcam" element={<Flcam />} />
            <Route path="/backup-restore" element={<BackupRestore />} />
            <Route path="/audit" element={<Audit />} />
          </Route>

          <Route path="/" element={<HomeRedirect />} />
          <Route path="*" element={<HomeRedirect />} />
        </Routes>
      </div>
    </Router>
  );
};

export default App;
